import React, { useEffect, useRef } from 'react';
import { Text, StyleSheet, Animated, ActivityIndicator } from 'react-native';

import {
  RequestStage,
  getStageDisplayName,
  getStageColor,
} from '../types/stages';

interface StageBadgeProps {
  stage: RequestStage | string | null | undefined;
  size?: 'small' | 'medium' | 'large';
  isUpdating?: boolean;
  animateChange?: boolean;
  /**
   * Show the internal-only "Configuration" label (PRD FR7). Opt-in and defaults to
   * false: the badge renders bare in its own suite and has no auth in scope, so the
   * viewer must be supplied by the screen. An unsupplied viewer is treated as external.
   */
  showInternalDetail?: boolean;
  testID?: string;
}

export const StageBadge: React.FC<StageBadgeProps> = ({
  stage,
  size = 'medium',
  isUpdating = false,
  animateChange = true,
  showInternalDetail = false,
  testID,
}) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;
  const previousStage = useRef(stage);

  useEffect(() => {
    if (
      stage &&
      animateChange &&
      previousStage.current !== stage &&
      previousStage.current !== null
    ) {
      // Only animate opacity change, no scaling to prevent size jumps
      Animated.sequence([
        Animated.timing(opacityAnim, {
          toValue: 0.7,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
    previousStage.current = stage;
  }, [stage, animateChange, scaleAnim, opacityAnim]);

  if (!stage) {
    return null;
  }

  const stageEnum = stage as RequestStage;
  const displayName = getStageDisplayName(stageEnum, showInternalDetail);
  const color = getStageColor(stageEnum, showInternalDetail);

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          badge: styles.badgeSmall,
          text: styles.textSmall,
        };
      case 'large':
        return {
          badge: styles.badgeLarge,
          text: styles.textLarge,
        };
      default:
        return {
          badge: styles.badgeMedium,
          text: styles.textMedium,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <Animated.View
      testID={testID}
      style={[
        styles.badge,
        sizeStyles.badge,
        { backgroundColor: color },
        {
          opacity: opacityAnim,
        },
        isUpdating && styles.updating,
      ]}
    >
      <Text style={[styles.text, sizeStyles.text]}>{displayName}</Text>
      {isUpdating && (
        <ActivityIndicator
          size="small"
          color="#FFFFFF"
          style={styles.spinnerOverlay}
        />
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  badge: {
    borderRadius: 12,
  },
  badgeSmall: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  badgeMedium: {
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  badgeLarge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  text: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  textSmall: {
    fontSize: 11,
  },
  textMedium: {
    fontSize: 12,
  },
  textLarge: {
    fontSize: 12,
  },
  updating: {
    opacity: 0.8,
  },
  spinnerOverlay: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: [{ translateY: -8 }], // Half of spinner height to center it
  },
});
